import React from 'react'
import ReactDOM from 'react-dom'
import { Provider, useDispatch } from 'react-redux'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import type { ModalRoute } from '@bcraft-frontend-lib/core'
import { ModalContextProvider } from '@bcraft-frontend-lib/core'
import { SLOTS_ROUTE_PATHS } from '@bcraft-frontend-lib/slots'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { AnyAction } from 'redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ThunkDispatch } from 'redux-thunk'
import { QueryParamProvider } from 'use-query-params'

import { configureStore, IApplicationState } from 'setup/store'

import { modalRoutes } from 'router/components/modalRoutes'

import {
  AuthModule,
  EmailsModule,
  HelpPageModule,
  ImportantRulesPageModule,
  MiniGameModule,
  PaymentsPageModule,
  ProfilePageModule,
  TermsPageModule,
  UpgamingSportModule
} from 'modules'
import { createRoutes } from 'modules/createRoutes'

import Notify from 'components/Notify/Notify'

import { SessionPolling } from 'components/SessionPolling'
import SetAppLanguage from 'components/SetAppLanguage/SetAppLanguage'

import App from './App'
import { AppProvider } from './AppProvider'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import { LegacySlotRedirect } from './modules/SlotsRoot/components/LegacySlotRedirect/LegacySlotRedirect'
import { SlotsFrameLayoutModule } from './modules/SlotsRoot/SlotsFrameLayoutModule'
import { SlotsLayoutModule } from './modules/SlotsRoot/SlotsLayoutModule'
import Api from './services/api'

import 'rsuite/dist/rsuite.min.css'
import 'react-toastify/dist/ReactToastify.css'
import '@bcraft-frontend-lib/core/lib/style.css'
import '@bcraft-frontend-lib/slots/lib/style.css'
import '@bcraft-frontend-lib/payments/lib/style.css'
import './styles/slots-frame-overrides.scss'
import './index.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

const api = new Api('')

const modules = [
  new AuthModule(),
  new ProfilePageModule(),
  new TermsPageModule(),
  new HelpPageModule(),
  new UpgamingSportModule(),
  new MiniGameModule(),
  new EmailsModule(),
  new ImportantRulesPageModule(),
  new SlotsLayoutModule(),
  new PaymentsPageModule()
]

const childrens = createRoutes(modules)

export const { store, persistor } = configureStore({ api })

const coreModalRoutes: ModalRoute[] = modalRoutes.flatMap(
  ({ route, Component, bgColor, borderRadius, hideCloseBtn, noPaddings, customProps, isStatic }) =>
    (Array.isArray(route) ? route : [route]).map(modalRoute => ({
      route: modalRoute,
      renderComponent: ({ path }) => <Component path={path} />,
      customProps: {
        bgColor,
        borderRadius,
        hideCloseBtn,
        noPaddings,
        ...customProps,
      },
      isStatic,
    })),
)

export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<ThunkDispatch<IApplicationState, any, AnyAction>>()

ReactDOM.render(
  <BrowserRouter>
    <QueryParamProvider ReactRouterRoute={Route}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <SetAppLanguage />
          <ErrorBoundary>
            <ModalContextProvider modalRoutes={coreModalRoutes}>
              <AppProvider>
                <Notify />
                <SessionPolling>
                  <Switch>
                    <Route path={SLOTS_ROUTE_PATHS.SLOTS_FRAME_ROOT}>
                      <SlotsFrameLayoutModule />
                    </Route>
                    <Route exact path="/slots/otc/:gameId" component={LegacySlotRedirect} />
                    <Route exact path="/slots/:sectionId/:gameId" component={LegacySlotRedirect} />
                    <Route exact path="/slots/:gameType/:provider/:gameId" component={LegacySlotRedirect} />
                    <Route path="/slots/:gameType/:provider/:gameId/:suffix" component={LegacySlotRedirect} />
                    <Route>
                      <App>{childrens}</App>
                    </Route>
                  </Switch>
                </SessionPolling>
              </AppProvider>
            </ModalContextProvider>
          </ErrorBoundary>
        </PersistGate>
      </Provider>
    </QueryParamProvider>
  </BrowserRouter>,
  document.getElementById('root'),
)
